import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { useShippingQuote } from "@/lib/shipping";
import { qtyDiscount, lineOriginal, lineTotal } from "@/lib/pricing";
import { Trash, Minus, Plus, ShoppingBag, ArrowRight, Warning } from "@phosphor-icons/react";
import { VialImage } from "@/components/VialImage";
import PaymentBadges from "@/components/PaymentBadges";
import { AddonPicks } from "@/components/AddonPicks";

export default function Cart() {
  const { items, remove, setQty, clear } = useCart();
  const [shipMethod, setShipMethod] = useState("standard");
  const [stockMap, setStockMap] = useState({});
  const [stockLoaded, setStockLoaded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/products")
      .then((r) => { const m = {}; r.data.forEach((p) => { m[p.id] = p.stock; }); setStockMap(m); setStockLoaded(true); })
      .catch(() => setStockLoaded(true));
  }, []);

  const stockIssues = stockLoaded ? items.filter((i) => (stockMap[i.product_id] ?? 0) < i.quantity) : [];
  const hasStockIssue = stockIssues.length > 0;

  const subtotalOriginal = items.reduce((s, i) => s + lineOriginal(i.price, i.quantity), 0);
  const total = items.reduce((s, i) => s + lineTotal(i.price, i.quantity), 0);
  const savings = subtotalOriginal - total;

  const quote = useShippingQuote(items, total);
  const freeThreshold = quote?.free_threshold ?? 180;
  const freeStandard = quote ? quote.standard === 0 : total >= freeThreshold;
  const shippingCost = quote ? quote[shipMethod] : 0;
  const grandTotal = total + shippingCost;

  const checkout = () => { if (hasStockIssue) return; nav(`/checkout?ship=${shipMethod}`); };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <ShoppingBag size={48} className="text-muted-foreground mx-auto mb-6" />
        <h1 className="font-head text-3xl mb-3">Your cart is empty</h1>
        <p className="text-muted-foreground mb-8">Explore our research peptide catalog to get started.</p>
        <Link to="/shop" data-testid="cart-empty-shop" className="inline-flex items-center gap-2 bg-primary text-black label-mono px-6 py-4">
          Browse Catalog <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <h1 className="font-head text-4xl mb-12">Shopping Cart</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-px">
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-5 bg-surface border border-black/10 p-5">
              <div className="w-24 h-24 shrink-0 border border-black/10 bg-white">
                <VialImage name={i.name} showName={false} />
              </div>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between">
                  <h3 className="font-head text-lg">{i.name}</h3>
                  <button data-testid={`remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={18} /></button>
                </div>
                <span className="font-mono text-sm text-muted-foreground mt-1">${i.price.toFixed(2)} each</span>
                {qtyDiscount(i.quantity) > 0 && (
                  <span data-testid={`bulk-badge-${i.product_id}`} className="mt-1 label-mono text-[0.55rem] text-primary border border-primary/40 bg-primary/10 px-2 py-0.5 w-fit">
                    Bulk −{Math.round(qtyDiscount(i.quantity) * 100)}%
                  </span>
                )}
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-black/15">
                    <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="p-2 hover:text-primary transition-colors"><Minus size={14} /></button>
                    <span className="font-mono w-8 text-center text-sm">{i.quantity}</span>
                    <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="p-2 hover:text-primary transition-colors"><Plus size={14} /></button>
                  </div>
                  <div className="text-right">
                    {qtyDiscount(i.quantity) > 0 && (
                      <span className="font-mono text-xs text-muted-foreground line-through mr-2">${lineOriginal(i.price, i.quantity).toFixed(2)}</span>
                    )}
                    <span className="font-mono text-lg">${lineTotal(i.price, i.quantity).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={clear} data-testid="clear-cart" className="label-mono text-muted-foreground hover:text-destructive transition-colors pt-4">Clear cart</button>
        </div>

        <div className="bg-surface border border-black/10 p-8 h-fit sticky top-24">
          <h2 className="font-head text-xl mb-6">Order Summary</h2>

          <div className="mb-6">
            <span className="label-mono text-muted-foreground block mb-3">Shipping Method</span>
            <div className="space-y-2">
              <button
                data-testid="ship-standard"
                onClick={() => setShipMethod("standard")}
                className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${shipMethod === "standard" ? "border-primary bg-primary/10" : "border-black/15 hover:border-black/30"}`}
                style={{ transition: "border-color 0.2s, background-color 0.2s" }}
              >
                <span className="text-sm">Standard</span>
                <span className="font-mono text-sm">{!quote ? "…" : freeStandard ? <span className="text-primary">FREE</span> : `$${quote.standard.toFixed(2)}`}</span>
              </button>
              <button
                data-testid="ship-express"
                onClick={() => setShipMethod("express")}
                className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${shipMethod === "express" ? "border-primary bg-primary/10" : "border-black/15 hover:border-black/30"}`}
                style={{ transition: "border-color 0.2s, background-color 0.2s" }}
              >
                <span className="text-sm">Express</span>
                <span data-testid="ship-express-price" className="font-mono text-sm">{quote ? `$${quote.express.toFixed(2)}` : "…"}</span>
              </button>
            </div>
            {quote && freeThreshold > 0 && (
              <div data-testid="free-ship-bar" className="mt-3">
                <div className="h-1.5 bg-white/10 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(100, (total / freeThreshold) * 100))}>
                  <div data-testid="free-ship-bar-fill" className="h-full bg-primary"
                    style={{ width: `${freeStandard ? 100 : Math.min(100, (total / freeThreshold) * 100)}%`, transition: "width 0.5s ease-out" }} />
                </div>
                <p data-testid="free-ship-hint" className={`text-[0.65rem] mt-2 ${freeStandard ? "text-primary" : "text-muted-foreground"}`}>
                  {freeStandard ? "You've unlocked FREE standard shipping" : `Add $${(freeThreshold - total).toFixed(2)} more for FREE standard shipping.`}
                </p>
                {!freeStandard && <AddonPicks gap={freeThreshold - total} />}
              </div>
            )}
          </div>

          <div data-testid="shipping-disclaimer" className="mb-6 text-[0.65rem] text-muted-foreground leading-relaxed border border-primary/25 bg-primary/5 p-3">
            <span className="label-mono text-primary block mb-1">Shipping Notice</span>
            We currently ship to the <span className="text-foreground">United States</span> only. For orders to <span className="text-foreground">Jamaica</span>, please email <a href="mailto:support@mulanpeptidecollection.com" className="text-primary hover:underline">support@mulanpeptidecollection.com</a>.
          </div>

          <div className="space-y-3 text-sm border-t border-black/10 pt-6">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${subtotalOriginal.toFixed(2)}</span></div>
            {savings > 0 && (
              <div className="flex justify-between" data-testid="bulk-savings"><span className="text-primary">Bulk discount</span><span className="font-mono text-primary">−${savings.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span data-testid="shipping-cost" className="font-mono">{!quote ? "…" : shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono text-muted-foreground">Calculated at checkout</span></div>
          </div>
          <div className="flex justify-between border-t border-black/10 mt-6 pt-6 mb-8">
            <span className="font-head text-lg">Total</span>
            <span data-testid="cart-total" className="font-mono text-2xl text-primary">${grandTotal.toFixed(2)}</span>
          </div>
          {hasStockIssue && (
            <div data-testid="cart-stock-warning" className="mb-4 border border-destructive/30 bg-destructive/5 p-3 text-[0.7rem] text-destructive leading-relaxed">
              <span className="label-mono flex items-center gap-1.5 mb-1"><Warning size={14} weight="bold" /> Stock issue</span>
              {stockIssues.map((i) => {
                const avail = stockMap[i.product_id] ?? 0;
                return (
                  <p key={i.product_id}>
                    {i.name}: {avail <= 0 ? "now out of stock" : `only ${avail} left`} — please reduce or remove it.
                  </p>
                );
              })}
            </div>
          )}
          <PaymentBadges />
          <button
            data-testid="checkout-btn"
            onClick={checkout}
            disabled={hasStockIssue}
            className="w-full flex items-center justify-center gap-2 bg-primary text-black label-mono py-4 hover:bg-[#E6C766] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ transition: "background-color 0.2s" }}
          >
            {hasStockIssue ? "Adjust cart to continue" : <>Secure Checkout <ArrowRight size={16} weight="bold" /></>}
          </button>
          <p className="label-mono text-muted-foreground text-[0.6rem] text-center mt-4">Pay by Cash App, Zelle, Google Pay or Apple Pay — instructions provided at checkout</p>
        </div>
      </div>
    </div>
  );
}
